/* eslint-disable */
import { createStore } from "vuex";
import offlineSearchService from "@/service/offlineSearch.service";
import dbService from "@/service/db.service";
import syncService from "@/service/sync.service";
import userStore from "@/store/user.store";

const abortController = new AbortController();
abortController.signal.addEventListener("abort", () => console.warn('request aborted'));

const getFavKey = () => {
    const userId = userStore?.state?.userInfo?.id || 'anon';
    return `favorites-user-${userId}`;
};

const store = createStore({
    state: {
        open: false,
        closed: true,
        modal: null,
        text: "",
        results: [],
        favorites: [],
        loadingFavorites: false,
        loading: false,
        bodyVisible: false,
        voiceSearch: false
    },
    mutations: {
        OPEN (state) {
            if (state.modal){
                state.modal.show();
                state.open = true;
                state.closed = !state.open;
                state.bodyVisible = state.loading || state.results.length > 0 || (!state.text && state.favorites && state.favorites.length >= 0);
            }
        },
        CLOSE (state) {
            if (state.modal){
                state.modal.hide();
                state.closed = true;
                state.results = [];
                state.open = !state.closed;
                state.bodyVisible = false;
                state.voiceSearch = false;
            }
        },
        VOICE_SEARCH (state) {
            state.voiceSearch = !state.voiceSearch;
        },
        SET_FAVORITES (state, favorites) {
            state.favorites = favorites || [];
            if (!state.text) {
                state.bodyVisible = true;
            }
        },
        SET_LOADING_FAVORITES (state, val) {
            state.loadingFavorites = val;
            if (val && !state.text) {
                state.bodyVisible = true;
            }
        }
    },
    actions: {
        open(context) {
            context.commit("OPEN");
            context.dispatch("fetchFavorites");
        },
        close(context) {
            context.state.text = "";
            context.commit("CLOSE");
        },
        async fetchFavorites(context) {
            context.commit("SET_LOADING_FAVORITES", true);
            const favKey = getFavKey();

            if (typeof navigator !== 'undefined' && !navigator.onLine) {
                try {
                    let cached = await dbService.getItem(favKey);
                    if (!cached) {
                        cached = await dbService.getItem('favorites');
                    }
                    context.commit("SET_FAVORITES", Array.isArray(cached) ? cached : []);
                } catch (e) {
                    console.warn('[SearchModalStore] Error loading offline favorites:', e);
                    context.commit("SET_FAVORITES", []);
                } finally {
                    context.commit("SET_LOADING_FAVORITES", false);
                }
                return;
            }

            try {
                const response = await fetch("/api/user/favorite-songs");
                if (response.ok) {
                    const data = await response.json();
                    const favList = Array.isArray(data) ? data : [];
                    context.commit("SET_FAVORITES", favList);
                    await dbService.setItem(favKey, favList);
                } else {
                    const cached = await dbService.getItem(favKey);
                    context.commit("SET_FAVORITES", Array.isArray(cached) ? cached : []);
                }
            } catch (e) {
                console.warn('[SearchModalStore] Fetch favorites failed, using cache:', e);
                try {
                    const cached = await dbService.getItem(favKey);
                    context.commit("SET_FAVORITES", Array.isArray(cached) ? cached : []);
                } catch (err) {
                    context.commit("SET_FAVORITES", []);
                }
            } finally {
                context.commit("SET_LOADING_FAVORITES", false);
            }
        },
        async toggleFavorite(context, item) {
            const songId = item.id || item._id;
            const isFav = context.state.favorites.find(f => (f.id || f._id) === songId);
            const favKey = getFavKey();

            if (typeof navigator !== 'undefined' && !navigator.onLine) {
                let updatedFavorites;
                if (isFav) {
                    updatedFavorites = context.state.favorites.filter(f => (f.id || f._id) !== songId);
                    syncService.enqueue({
                        url: `/api/user/favorite-songs/${songId}`,
                        method: 'DELETE',
                        type: 'favorite'
                    });
                } else {
                    updatedFavorites = [...context.state.favorites, item];
                    syncService.enqueue({
                        url: `/api/user/favorite-songs`,
                        method: 'POST',
                        body: { songId },
                        type: 'favorite'
                    });
                }
                context.commit("SET_FAVORITES", updatedFavorites);
                await dbService.setItem(favKey, updatedFavorites);
                return;
            }

            try {
                if (isFav) {
                    const response = await fetch(`/api/user/favorite-songs/${songId}`, { method: 'DELETE' });
                    if (response.ok) {
                        const updated = context.state.favorites.filter(f => (f.id || f._id) !== songId);
                        context.commit("SET_FAVORITES", updated);
                        await dbService.setItem(favKey, updated);
                    }
                } else {
                    const response = await fetch(`/api/user/favorite-songs`, { 
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ songId })
                    });
                    if (response.ok) {
                        context.dispatch("fetchFavorites");
                    }
                }
            } catch (e) {
                console.warn('[SearchModalStore] Network error toggling favorite, enqueuing sync:', e);
                let updatedFavorites;
                if (isFav) {
                    updatedFavorites = context.state.favorites.filter(f => (f.id || f._id) !== songId);
                    syncService.enqueue({
                        url: `/api/user/favorite-songs/${songId}`,
                        method: 'DELETE',
                        type: 'favorite'
                    });
                } else {
                    updatedFavorites = [...context.state.favorites, item];
                    syncService.enqueue({
                        url: `/api/user/favorite-songs`,
                        method: 'POST',
                        body: { songId },
                        type: 'favorite'
                    });
                }
                context.commit("SET_FAVORITES", updatedFavorites);
                await dbService.setItem(favKey, updatedFavorites);
            }
        },
        typing(context, options = {}) {
            context.commit("OPEN");
            if (context.state.text.length > 2){
                context.state.loading = true;
                const page = options.page || 0;

                if (typeof navigator !== 'undefined' && !navigator.onLine) {
                    offlineSearchService.search(context.state.text, { page, limit: 20 })
                        .then(results => {
                            if (page > 0) {
                                context.state.results = [...context.state.results, ...results];
                            } else {
                                context.state.results = results;
                            }
                        })
                        .catch(err => {
                            console.error('[SearchModalStore] Offline search error:', err);
                            if (page === 0) context.state.results = [];
                        })
                        .finally(() => {
                            context.state.loading = false;
                            context.state.bodyVisible = context.state.loading || context.state.results.length > 0;
                        });
                    context.state.bodyVisible = true;
                    return;
                }

                fetch("/api/search", {
                    signal: abortController.signal,
                    method: "POST",
                    body: JSON.stringify({
                        search: context.state.text,
                        page: page
                    }), 
                    headers: {
                        "content-type": "application/json"
                    }
                }).then( async (result) => {
                    if (!result.ok) throw new Error('Search network error');
                    let data = await result.json();
                    if (page > 0) {
                        context.state.results = [...context.state.results, ...data];
                    } else {
                        context.state.results = data;
                    }
                }).catch( async (e) => {
                    console.warn('[SearchModalStore] Online search error, falling back to offline search:', e);
                    try {
                        const results = await offlineSearchService.search(context.state.text, { page, limit: 20 });
                        if (page > 0) {
                            context.state.results = [...context.state.results, ...results];
                        } else {
                            context.state.results = results;
                        }
                    } catch (offErr) {
                        console.error('[SearchModalStore] Offline search fallback error:', offErr);
                    }
                }).finally( () =>{
                    context.state.loading = false;
                    context.state.bodyVisible = context.state.loading || context.state.results.length > 0;
                });
                context.state.bodyVisible = context.state.loading || context.state.results.length > 0;
            } else {
                context.state.results = [];
                if (!context.state.text) {
                    context.state.bodyVisible = true;
                } else {
                    context.state.bodyVisible = false;
                }
            }
        },
        voiceSearch(context) {
            context.commit("VOICE_SEARCH");
        }
    }
});

export default store;